import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import axios from "axios";
import { baseUrl } from "../api";
import {
  Container,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
  Box,
  Grid,
} from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { AuthContext } from "../context/AuthContext";
import { CourseContext } from "../context/CourseContext";
import styles from "../styles/courseEditorStyles";

// Helper function to parse <head> and <body> from a full HTML string
const parseHTML = (htmlString) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const head = doc.querySelector("head")?.innerHTML || "";
  const body = doc.querySelector("body")?.innerHTML || "";
  return { head, body };
};

// Helper function to build full HTML from head/body
const buildFullHTML = (headContent, bodyContent) => {
  return `
    <html>
      <head>
        ${headContent}
      </head>
      <body>
        ${bodyContent}
      </body>
    </html>
  `;
};

// Helper function to extract display name from a GCS link.
const extractNameFromLink = (gcsLink) => {
  const parts = gcsLink.split("/");
  const filename = parts[parts.length - 1];
  return filename.replace(".html", "");
};

// Helper to add a data attribute to all iframes and anchor tags in a given HTML string.
const addDataAttributeToElements = (htmlString, dataAttr, value) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  doc.querySelectorAll("iframe, a").forEach((el) => {
    el.setAttribute(dataAttr, value);
  });
  return doc.body.innerHTML;
};

// Helper to remove elements from an HTML string that have a given data attribute value.
const removeElementByDataAttribute = (htmlString, dataAttr, value) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const elements = doc.querySelectorAll(`[${dataAttr}="${value}"]`);
  elements.forEach((el) => el.remove());
  return doc.body.innerHTML;
};

const CourseEditor = () => {
  const { user } = useContext(AuthContext);
  // Consume the global course and lesson state from CourseContext.
  const { selectedCourse, setSelectedCourse, selectedLesson, setSelectedLesson } =
    useContext(CourseContext);

  // Create an Axios instance with an interceptor to attach the Bearer token
  const axiosInstance = useMemo(() => {
    const instance = axios.create({
      baseURL: baseUrl,
    });
    instance.interceptors.request.use(
      async (config) => {
        if (user) {
          const token = await user.getIdToken();
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
    return instance;
  }, [user]);

  // ========== Local state for courses and lessons list ==========
  const [courses, setCourses] = useState([]);
  const [lessons, setLessons] = useState([]);

  // ========== State for editing type ==========
  const [editingType, setEditingType] = useState("lesson");
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [selectedVisualization, setSelectedVisualization] = useState(null);
  const [selectedDeepdive, setSelectedDeepdive] = useState(null);
  const [selectedCoding, setSelectedCoding] = useState(null);

  // ========== State for HEAD/BODY content ==========
  const [headContent, setHeadContent] = useState("");
  const [bodyContent, setBodyContent] = useState("");

  // ========== Original HEAD/BODY for resetting ==========
  const [originalHeadContent, setOriginalHeadContent] = useState("");
  const [originalBodyContent, setOriginalBodyContent] = useState("");

  // ========== New state for raw HTML ==========
  const [rawHTML, setRawHTML] = useState("");

  // ========== New state for local base URL (only for rendering) ==========
  const [contentBaseUrl, setContentBaseUrl] = useState("");

  // ========== State for Quiz/Visualization/Deepdive Lists & Modals ==========
  const [quizList, setQuizList] = useState([]);
  const [visualizationList, setVisualizationList] = useState([]);
  const [deepdiveList, setDeepdiveList] = useState([]);
  const [codingList, setCodingList] = useState([]);
  
  
  const [openQuizModal, setOpenQuizModal] = useState(false);
  const [openVisualizationModal, setOpenVisualizationModal] = useState(false);
  const [openDeepdiveModal, setOpenDeepdiveModal] = useState(false);
  const [openCodingModal, setOpenCodingModal] = useState(false);

  // ========== Iframe reference for live preview ==========
  const iframeRef = useRef(null);

  // ================== Fetch courses on mount ==================
  useEffect(() => {
    fetchCourses();
  }, []);

  // ================== Refresh lessons when the global selectedCourse changes ==================
  useEffect(() => {
    if (selectedCourse) {
      fetchLessons(selectedCourse);
      // Do not clear the lesson if one is already selected (preserves selection when coming from another screen)
      // If the selected lesson does not belong to the new course, you might want to clear it.
      // For now we assume that switching from another screen has a matching selected lesson.
    } else {
      setLessons([]);
    }
  }, [selectedCourse]);

  // ================== Refresh lesson content when selectedLesson and lessons are available ==================
  useEffect(() => {
    if (selectedLesson && lessons.length > 0) {
      fetchLessonContent(selectedLesson);
    }
  }, [selectedLesson, lessons]);

  // ================== Live Preview in Iframe ==================
  useEffect(() => {
    if (!iframeRef.current) return;
    const iframeDoc =
      iframeRef.current.contentDocument ||
      iframeRef.current.contentWindow.document;
    iframeDoc.open();

    // Inject a <base> tag for local rendering if contentBaseUrl is set.
    let modifiedHead = headContent;
    if (contentBaseUrl && !headContent.includes("<base")) {
      modifiedHead = `<base href="${contentBaseUrl}" />` + headContent;
    }
    const htmlToRender = buildFullHTML(modifiedHead, bodyContent);
    iframeDoc.write(htmlToRender);
    iframeDoc.close();
    if (iframeDoc.defaultView && iframeDoc.defaultView.MathJax) {
      iframeDoc.defaultView.MathJax.typesetPromise();
    }

    const stopAnchorNav = (e) => {
      const link = (e.target).closest('a');
      if (!link) return;
  
      const isPopup =
        link.getAttribute('target') === '_blank' ||   // normal pop-up
        link.dataset.allowPopup === 'true';           // optional custom flag
  
      if (!isPopup) {
        e.preventDefault();      // keep the frame where it is
        e.stopPropagation();     // don’t let other handlers run
      }
    };

    iframeDoc.addEventListener('click', stopAnchorNav, /* useCapture */ true);
    return () => {
      iframeDoc.removeEventListener('click', stopAnchorNav, true);
    };
  }, [headContent, bodyContent, contentBaseUrl]);

  // ================== API Calls ==================
  const fetchCourses = async () => {
    try {
      const response = await axiosInstance.get("/courses/");
      setCourses(response.data);
    } catch (error) {
      console.error("Error fetching courses:", error);
    }
  };

  const fetchLessons = async (courseId) => {
    try {
      const response = await axiosInstance.get(`/courses/${courseId}`);
      setLessons(response.data.lessons);
    } catch (error) {
      console.error("Error fetching lessons:", error);
    }
  };

  const fetchLessonContent = async (lessonId) => {
    try {
      const response = await axiosInstance.get(`/lessons/${lessonId}`);
      const fullHTML = response.data.content;
      const { head, body } = parseHTML(fullHTML);
      setHeadContent(head);
      setBodyContent(body);
      setOriginalHeadContent(head);
      setOriginalBodyContent(body);

      // Set contentBaseUrl from content_gcp_path if the lesson is found in the refreshed lessons array
      const lessonObj = lessons.find((l) => l.id == lessonId);
      if (lessonObj && lessonObj.content_gcp_path) {
        let fullPath = lessonObj.content_gcp_path;
        const lastSlashIndex = fullPath.lastIndexOf("/");
        if (lastSlashIndex !== -1) {
          fullPath = fullPath.substring(0, lastSlashIndex + 1);
        }
        setContentBaseUrl(fullPath);
      } else {
        setContentBaseUrl("");
      }
      
      setRawHTML(buildFullHTML(head, body));
    } catch (error) {
      console.error("Error fetching lesson content:", error);
    }
  };

  const fetchQuizContent = async (quizId) => {
    try {
      const response = await axiosInstance.get(`/assets/quizzes/${quizId}`);
      const fullHTML = response.data.content;
      const { head, body } = parseHTML(fullHTML);
      const updatedBody = addDataAttributeToElements(body, "data-quiz-id", quizId);
      setHeadContent(head);
      setBodyContent(updatedBody);
      setOriginalHeadContent(head);
      setOriginalBodyContent(updatedBody);
      setRawHTML(buildFullHTML(head, updatedBody));
    } catch (error) {
      console.error("Error fetching quiz content:", error);
    }
  };

  const fetchVisualizationContent = async (visualId) => {
    try {
      const response = await axiosInstance.get(`/assets/visuals/${visualId}`);
      const fullHTML = response.data.content;
      const { head, body } = parseHTML(fullHTML);
      const updatedBody = addDataAttributeToElements(body, "data-visual-id", visualId);
      setHeadContent(head);
      setBodyContent(updatedBody);
      setOriginalHeadContent(head);
      setOriginalBodyContent(updatedBody);
      setRawHTML(buildFullHTML(head, updatedBody));
    } catch (error) {
      console.error("Error fetching visualization content:", error);
    }
  };

  const fetchDeepdiveContent = async (deepdiveId) => {
    try {
      const response = await axiosInstance.get(`/assets/deepdives/${deepdiveId}`);
      const fullHTML = response.data.content;
      const { head, body } = parseHTML(fullHTML);
      const updatedBody = addDataAttributeToElements(body, "data-visual-id", deepdiveId);
      setHeadContent(head);
      setBodyContent(updatedBody);
      setOriginalHeadContent(head);
      setOriginalBodyContent(updatedBody);
      setRawHTML(buildFullHTML(head, updatedBody));
    } catch (error) {
      console.error("Error fetching deep dive content:", error);
    }
  };

  const fetchCodingContent = async (resourceId) => {
    try {
      const response = await axiosInstance.get(`/assets/codings/${resourceId}`);
      const fullHTML = response.data.content;
      const { head, body } = parseHTML(fullHTML);
      const updatedBody = addDataAttributeToElements(body, "data-visual-id", resourceId);
      setHeadContent(head);
      setBodyContent(updatedBody);
      setOriginalHeadContent(head);
      setOriginalBodyContent(updatedBody);
      setRawHTML(buildFullHTML(head, updatedBody));
    } catch (error) {
      console.error("Error fetching deep dive content:", error);
    }
  };

  const fetchCourseContent = async () => {
    try {
      const response = await axiosInstance.get(`/course_content/${selectedCourse}`);
      const fullHTML = response.data;
      const { head, body } = parseHTML(fullHTML);
      setHeadContent(head);
      setBodyContent(body);
      setOriginalHeadContent(head);
      setOriginalBodyContent(body);
      setRawHTML(buildFullHTML(head, body));
    } catch (error) {
      console.error("Error fetching course content:", error);
    }
  };

  const fetchQuizList = async () => {
    if (!selectedCourse || !selectedLesson) {
      alert("Please select a course and lesson first.");
      return;
    }
    try {
      const response = await axiosInstance.get("/assets/quizzes", {
        params: { course_id: selectedCourse, lesson_id: selectedLesson },
      });
      setQuizList(response.data);
    } catch (error) {
      console.error("Error fetching quiz list:", error);
    }
  };

  const fetchVisualizationList = async () => {
    if (!selectedCourse || !selectedLesson) {
      alert("Please select a course and lesson first.");
      return;
    }
    try {
      const response = await axiosInstance.get("/assets/visuals", {
        params: { course_id: selectedCourse, lesson_id: selectedLesson },
      });
      setVisualizationList(response.data);
    } catch (error) {
      console.error("Error fetching visualization list:", error);
    }
  };

  const fetchDeepdiveList = async () => {
    if (!selectedCourse || !selectedLesson) {
      alert("Please select a course and lesson first.");
      return;
    }
    try {
      const response = await axiosInstance.get("/assets/deepdives", {
        params: { course_id: selectedCourse, lesson_id: selectedLesson },
      });
      setDeepdiveList(response.data);
    } catch (error) {
      console.error("Error fetching deep dive list:", error);
    }
  };

  const fetchCodingList = async () => {
    if (!selectedCourse || !selectedLesson) {
      alert("Please select a course and lesson first.");
      return;
    }
    try {
      const response = await axiosInstance.get("/assets/codings", {
        params: { course_id: selectedCourse, lesson_id: selectedLesson },
      });
      setCodingList(response.data);
    } catch (error) {
      console.error("Error fetching deep dive list:", error);
    }
  };

  // ================== Delete Handlers ==================
  const deleteQuiz = async (quiz) => {
    if (window.confirm("Are you sure you want to delete this quiz?")) {
      try {
        await axiosInstance.delete(`/assets/quizzes/${quiz.visual_id}`);
        const updatedBody = removeElementByDataAttribute(bodyContent, "data-quiz-id", quiz.visual_id);
        setBodyContent(updatedBody);
        setRawHTML(buildFullHTML(headContent, updatedBody));
        alert("Quiz deleted successfully");
        fetchQuizList();
      } catch (error) {
        console.error("Error deleting quiz:", error);
        alert("Failed to delete quiz");
      }
    }
  };

  const deleteVisualization = async (vis) => {
    if (window.confirm("Are you sure you want to delete this visualization?")) {
      try {
        await axiosInstance.delete(`/assets/visuals/${vis.visual_id}`);
        const updatedBody = removeElementByDataAttribute(bodyContent, "data-visual-id", vis.visual_id);
        setBodyContent(updatedBody);
        setRawHTML(buildFullHTML(headContent, updatedBody));
        alert("Visualization deleted successfully");
        fetchVisualizationList();
      } catch (error) {
        console.error("Error deleting visualization:", error);
        alert("Failed to delete visualization");
      }
    }
  };

  const deleteDeepdive = async (deepdive) => {
    if (window.confirm("Are you sure you want to delete this deep dive explanation?")) {
      try {
        await axiosInstance.delete(`/assets/deepdives/${deepdive.visual_id}`);
        const updatedBody = removeElementByDataAttribute(bodyContent, "data-visual-id", deepdive.visual_id);
        setBodyContent(updatedBody);
        setRawHTML(buildFullHTML(headContent, updatedBody));
        alert("Deep dive explanation deleted successfully");
        fetchDeepdiveList();
      } catch (error) {
        console.error("Error deleting deep dive explanation:", error);
        alert("Failed to delete deep dive explanation");
      }
    }
  };

  const deleteCoding = async (coding) => {
    if (window.confirm("Are you sure you want to delete this deep dive explanation?")) {
      try {
        await axiosInstance.delete(`/assets/codings/${coding.visual_id}`);
        const updatedBody = removeElementByDataAttribute(bodyContent, "data-visual-id", coding.visual_id);
        setBodyContent(updatedBody);
        setRawHTML(buildFullHTML(headContent, updatedBody));
        alert("Deep dive explanation deleted successfully");
        fetchDeepdiveList();
      } catch (error) {
        console.error("Error deleting deep dive explanation:", error);
        alert("Failed to delete deep dive explanation");
      }
    }
  };

  // ================== Handlers ==================
  const handleCourseChange = (e) => {
    const courseId = e.target.value;
    // Update global course state
    setSelectedCourse(courseId);
    // Reset the global lesson state only if the new course differs from before.
    // (Do not clear selectedLesson if one already exists from another screen.)
    setLessons([]);
    setEditingType("lesson");
  };

  const handleLessonChange = (e) => {
    const lessonId = e.target.value;
    // Update global lesson state (fetching lesson content will be handled by the effect)
    setSelectedLesson(lessonId);
    setEditingType("lesson");
  };

  const saveContent = async () => {
    if (editingType === "lesson" && !selectedLesson) {
      alert("Please select a lesson to update.");
      return;
    } else if (editingType === "quiz" && !selectedQuiz) {
      alert("Please select a quiz to update.");
      return;
    } else if (editingType === "visualization" && !selectedVisualization) {
      alert("Please select a visualization to update.");
      return;
    } else if (editingType === "deepdive" && !selectedDeepdive) {
      alert("Please select a deep dive explanation to update.");
      return;
    } else if (editingType === "course" && !selectedCourse) {
      alert("Please select a course to update.");
      return;
    } else if (editingType === "coding" && !selectedCoding) {
      alert("Please select a coding to update.");
      return;
    }
    try {
      const fullHtml = buildFullHTML(headContent, bodyContent);
      let endpoint = "";
      if (editingType === "lesson") {
        endpoint = `/lessons/${selectedLesson}`;
      } else if (editingType === "quiz") {
        endpoint = `/assets/quizzes/${selectedQuiz}`;
      } else if (editingType === "visualization") {
        endpoint = `/assets/visuals/${selectedVisualization}`;
      } else if (editingType === "deepdive") {
        endpoint = `/assets/deepdives/${selectedDeepdive}`;
      } else if (editingType === "coding") {
        endpoint = `/assets/codings/${selectedCoding}`;        
      } else if (editingType === "course") {
        endpoint = `/course_content/${selectedCourse}`;
      }
      await axiosInstance.put(endpoint, { content: fullHtml });
      setOriginalHeadContent(headContent);
      setOriginalBodyContent(bodyContent);
      alert(
        `${editingType.charAt(0).toUpperCase() + editingType.slice(1)} content updated successfully`
      );
    } catch (error) {
      console.error(`Error updating ${editingType} content:`, error);
      alert(`Failed to update ${editingType} content`);
    }
  };

  const resetContent = () => {
    setHeadContent(originalHeadContent);
    setBodyContent(originalBodyContent);
    setRawHTML(buildFullHTML(originalHeadContent, originalBodyContent));
  };

  const handleRawChange = (e) => {
    const newRawHTML = e.target.value;
    setRawHTML(newRawHTML);
    const { head, body } = parseHTML(newRawHTML);
    setHeadContent(head);
    setBodyContent(body);
  };

  const deleteCourse = async () => {
    if (!selectedCourse) return;
    if (
      window.confirm(
        `Are you sure you want to delete the selected course (${selectedCourse})? This will delete all its lessons and associated files.`
      )
    ) {
      try {
        await axiosInstance.delete(`/courses/${selectedCourse}`);
        alert("Course deleted successfully");
        // Reset global state after deletion
        setSelectedCourse(null);
        setSelectedLesson(null);
        setLessons([]);
        fetchCourses();
      } catch (error) {
        console.error("Error deleting course:", error);
        alert("Failed to delete course");
      }
    }
  };

  const deleteLesson = async () => {
    if (!selectedLesson) return;
    if (window.confirm("Are you sure you want to delete the selected lesson?")) {
      try {
        await axiosInstance.delete(`/lessons/${selectedLesson}`);
        alert("Lesson deleted successfully");
        // Reset global lesson state after deletion
        setSelectedLesson(null);
        fetchLessons(selectedCourse);
      } catch (error) {
        console.error("Error deleting lesson:", error);
        alert("Failed to delete lesson");
      }
    }
  };

  const isContentChanged =
    headContent !== originalHeadContent || bodyContent !== originalBodyContent;

  return (
    <Container maxWidth={false} sx={{ maxWidth: 2000 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Course Selection Row */}
        <div style={styles.selectRow}>
          <div style={styles.selectContainer}>
            <label style={styles.label}>Select Course:</label>
            <select
              onChange={handleCourseChange}
              value={selectedCourse ? selectedCourse.toString() : ""}
              style={styles.select}
            >
              <option value="">Choose a course to edit</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id.toString()}>
                  {course.subject}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Lesson Selection Row */}
        <div style={styles.selectRow}>
          <div style={styles.selectContainer}>
            <label style={styles.label}>Select Lesson:</label>
            <select
              onChange={handleLessonChange}
              value={selectedLesson ? selectedLesson.toString() : ""}
              style={styles.select}
              disabled={!selectedCourse}
            >
              <option value="">Select a lesson from the course</option>
              {lessons.map((lesson) => (
                <option key={lesson.id} value={lesson.id.toString()}>
                  {lesson.title}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Danger Zone Section using Accordion */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ backgroundColor: "#ffebee" }}
          >
            <Typography sx={{ color: "#f44336", fontWeight: "bold" }}>
              Danger Zone
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div style={{ display: "flex", gap: "10px" }}>
              <Button
                onClick={deleteCourse}
                disabled={!selectedCourse}
                variant="contained"
                size="small"
                color="error"
              >
                Delete Course
              </Button>
              <Button
                onClick={deleteLesson}
                disabled={!selectedLesson}
                variant="contained"
                size="small"
                color="error"
              >
                Delete Lesson
              </Button>
            </div>
          </AccordionDetails>
        </Accordion>

        {/* Edit Actions Row */}
        <div style={styles.editActionsContainer}>
          <div style={styles.editButtonContainer}>
            <button
              onClick={() => {
                if (!selectedCourse) {
                  alert("Select a course first");
                  return;
                }
                setEditingType("course");
                fetchCourseContent();
              }}
              style={
                editingType === "course"
                  ? { ...styles.button, ...styles.activeButton }
                  : styles.button
              }
            >
              Edit Table of Contents
            </button>
            <button
              onClick={() => {
                if (!selectedLesson) {
                  alert("Select a lesson first");
                  return;
                }
                setEditingType("lesson");
                fetchLessonContent(selectedLesson); // Explicitly refresh lesson content
              }}
              style={
                editingType === "lesson"
                  ? { ...styles.button, ...styles.activeButton }
                  : styles.button
              }
            >
              Edit Lesson
            </button>
            <button
              onClick={() => {
                if (!selectedLesson) {
                  alert("Select a lesson first");
                  return;
                }
                fetchQuizList();
                setOpenQuizModal(true);
              }}
              style={
                editingType === "quiz"
                  ? { ...styles.button, ...styles.activeButton }
                  : styles.button
              }
            >
              Edit Quiz
            </button>
            <button
              onClick={() => {
                if (!selectedLesson) {
                  alert("Select a lesson first");
                  return;
                }
                fetchVisualizationList();
                setOpenVisualizationModal(true);
              }}
              style={
                editingType === "visualization"
                  ? { ...styles.button, ...styles.activeButton }
                  : styles.button
              }
            >
              Edit Visualization
            </button>
            <button
              onClick={() => {
                if (!selectedLesson) {
                  alert("Select a lesson first");
                  return;
                }
                fetchDeepdiveList();
                setOpenDeepdiveModal(true);
              }}
              style={
                editingType === "deepdive"
                  ? { ...styles.button, ...styles.activeButton }
                  : styles.button
              }
            >
              Edit Deep Dive
            </button>
            <button
              onClick={() => {
                if (!selectedLesson) {
                  alert("Select a lesson first");
                  return;
                }
                fetchCodingList();
                setOpenCodingModal(true);
              }}
              style={
                editingType === "coding"
                  ? { ...styles.button, ...styles.activeButton }
                  : styles.button
              }
            >
              Edit Coding
            </button>
          </div>
          <div style={styles.saveResetContainer}>
            <button
              onClick={saveContent}
              style={styles.button}
              disabled={!isContentChanged}
            >
              Save Changes
            </button>
            <button onClick={resetContent} style={styles.resetButton}>
              Reset to Last Saved State
            </button>
          </div>
        </div>

        {/* Display current editing type */}
        <div style={{ marginBottom: "10px" }}>
          <strong>Currently Editing:</strong>{" "}
          {editingType === "lesson" && selectedLesson
            ? lessons.find((l) => l.id == selectedLesson)?.title || "Lesson"
            : editingType.charAt(0).toUpperCase() + editingType.slice(1)}
        </div>

        {/* Editor & Preview Section using Grid for vertical alignment */}
        <Grid container spacing={2} alignItems="start" sx={{ marginTop: "20px" }}>
          {/* Left Panel: Raw HTML Editor */}
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="h6" sx={{ mb: 1, fontSize: "18px" }}>
                {editingType.charAt(0).toUpperCase() + editingType.slice(1)} Content (Raw HTML)
              </Typography>
              <textarea
                style={styles.textarea}
                value={rawHTML}
                onChange={handleRawChange}
              />
            </Box>
          </Grid>
          {/* Right Panel: Rendered Preview */}
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="h6" sx={{ mb: 1, fontSize: "18px" }}>
                Rendered Page
              </Typography>
              <iframe ref={iframeRef} title="Content Preview" style={styles.iframe} />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Quiz Selection Modal */}
      <Dialog open={openQuizModal} onClose={() => setOpenQuizModal(false)}>
        <DialogTitle>Select Quiz to Edit</DialogTitle>
        <DialogContent>
          <List>
            {quizList.map((quiz) => (
              <ListItem
                key={quiz.visual_id}
                secondaryAction={
                  <>
                    <IconButton edge="end" onClick={() => deleteQuiz(quiz)}>
                      <DeleteIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => {
                        setSelectedQuiz(quiz.visual_id);
                        setEditingType("quiz");
                        fetchQuizContent(quiz.visual_id);
                        setOpenQuizModal(false);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </>
                }
              >
                <ListItemText
                  primary={`Quiz: ${extractNameFromLink(quiz.gcs_link)}`}
                />
              </ListItem>
            ))}
            {quizList.length === 0 && <p>No quizzes found for this lesson.</p>}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenQuizModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Visualization Selection Modal */}
      <Dialog
        open={openVisualizationModal}
        onClose={() => setOpenVisualizationModal(false)}
      >
        <DialogTitle>Select Visualization to Edit</DialogTitle>
        <DialogContent>
          <List>
            {visualizationList.map((vis) => (
              <ListItem
                key={vis.visual_id}
                secondaryAction={
                  <>
                    <IconButton edge="end" onClick={() => deleteVisualization(vis)}>
                      <DeleteIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => {
                        setSelectedVisualization(vis.visual_id);
                        setEditingType("visualization");
                        fetchVisualizationContent(vis.visual_id);
                        setOpenVisualizationModal(false);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </>
                }
              >
                <ListItemText
                  primary={`Visual: ${extractNameFromLink(vis.gcs_link)}`}
                />
              </ListItem>
            ))}
            {visualizationList.length === 0 && (
              <p>No visuals found for this lesson.</p>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenVisualizationModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Deep Dive Selection Modal */}
      <Dialog open={openDeepdiveModal} onClose={() => setOpenDeepdiveModal(false)}>
        <DialogTitle>Select Deep Dive to Edit</DialogTitle>
        <DialogContent>
          <List>
            {deepdiveList.map((deepdive) => (
              <ListItem
                key={deepdive.visual_id}
                secondaryAction={
                  <>
                    <IconButton edge="end" onClick={() => deleteDeepdive(deepdive)}>
                      <DeleteIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => {
                        setSelectedDeepdive(deepdive.visual_id);
                        setEditingType("deepdive");
                        fetchDeepdiveContent(deepdive.visual_id);
                        setOpenDeepdiveModal(false);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </>
                }
              >
                <ListItemText
                  primary={`Deep Dive: ${extractNameFromLink(deepdive.gcs_link)}`}
                />
              </ListItem>
            ))}
            {deepdiveList.length === 0 && <p>No deep dive explanations found for this lesson.</p>}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeepdiveModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* Deep Dive Selection Modal */}
      <Dialog open={openCodingModal} onClose={() => setOpenCodingModal(false)}>
        <DialogTitle>Select Coding to Edit</DialogTitle>
        <DialogContent>
          <List>
            {codingList.map((coding) => (
              <ListItem
                key={coding.visual_id}
                secondaryAction={
                  <>
                    <IconButton edge="end" onClick={() => deleteCoding(coding)}>
                      <DeleteIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => {
                        setSelectedCoding(coding.visual_id);
                        setEditingType("coding");
                        fetchCodingContent(coding.visual_id);
                        setOpenCodingModal(false);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </>
                }
              >
                <ListItemText
                  primary={`Deep Dive: ${extractNameFromLink(coding.gcs_link)}`}
                />
              </ListItem>
            ))}
            {codingList.length === 0 && <p>No coding found for this lesson.</p>}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCodingModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default CourseEditor;
